/**
 * MinistrareIA - Agents Page
 * InfinitAgents integration - List agents, configure autopilot
 *
 * UPDATED:
 * - Popup config when clicking agent (autopilot mode + contacts list)
 * - Fixed empty state: redirect to onboarding when no API key or no agents
 * - Bidirectional sync with chat toggle
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Loader2, AlertCircle, CheckCircle2, X, Settings, Zap } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/global/authService';

const AgentsPage = ({ isNavOpen, onToggleNav }) => {
  const { colors } = useTheme();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  // State
  const [agents, setAgents] = useState([]);
  const [activeAgentId, setActiveAgentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Popup state
  const [showPopup, setShowPopup] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [autopilotMode, setAutopilotMode] = useState('off');
  const [autopilotContacts, setAutopilotContacts] = useState([]);
  const [saving, setSaving] = useState(false);




  /**
   * Fetch agents from InfinitAgents API
   */
  const fetchAgents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.getAgents();
      setAgents(response.data || []);
      setActiveAgentId(response.data.active_agent_id);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Erro ao carregar agentes';
      setError(errorMessage);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load autopilot config
   */
  const loadAutopilotConfig = useCallback(async () => {
    try {
      const response = await authService.getAutopilotConfig();
      if (response?.data) {
        setAutopilotMode(response.data.autopilot_mode || 'off');
        setAutopilotContacts(response.data.autopilot_contacts || []);
      }
    } catch (err) {
      console.error('Error loading autopilot config:', err);
    }
  }, []);

  useEffect(() => {
    const hasApiKey = !!user?.infinitagents_api_key;
    if (hasApiKey) {
      fetchAgents();
      loadAutopilotConfig();
    } else {
      setLoading(false);
    }
  }, [user?.infinitagents_api_key, fetchAgents, loadAutopilotConfig]);

  /**
   * Handle agent card click → open popup
   */
  const handleAgentClick = (agent) => {
    setSelectedAgent(agent);
    setShowPopup(true);
  };

  /**
   * Handle "Configurar agente" button → redirect to onboarding
   */
  const handleSetupAgent = () => {
    // Check localStorage for cached onboarding progress
    const cache = localStorage.getItem('lumensales-onboarding-cache');
    if (cache) {
      try {
        const parsed = JSON.parse(cache);
        const savedAt = new Date(parsed.savedAt);
        const daysSince = (Date.now() - savedAt.getTime()) / (1000 * 60 * 60 * 24);
        // If cache is older than 7 days, clear it
        if (daysSince > 7) {
          localStorage.removeItem('lumensales-onboarding-cache');
        }
      } catch {
        localStorage.removeItem('lumensales-onboarding-cache');
      }
    }
    navigate('/agent-onboarding');
  };

  /**
   * Save autopilot config from popup
   */
  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const response = await authService.updateAutopilotConfig(autopilotMode, autopilotContacts);
      if (response?.data) {
        setAutopilotMode(response.data.autopilot_mode || 'off');
        setAutopilotContacts(response.data.autopilot_contacts || []);
      }
      setShowPopup(false);
    } catch (err) {
      console.error('Error saving autopilot config:', err);
    } finally {
      setSaving(false);
    }
  };




  /**
   * Get avatar initials from agent name
   */
  const getInitials = (name) => {
    if (!name) return 'AG';
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  /**
   * Get avatar color based on agent name (consistent color per agent)
   */
  const getAvatarColor = (name) => {
    const avatarColors = [
      '#8B5CF6', '#EC4899', '#3B82F6', '#10B981',
      '#F59E0B', '#EF4444', '#06B6D4', '#6366F1',
    ];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return avatarColors[Math.abs(hash) % avatarColors.length];
  };

  /**
   * Truncate text with ellipsis
   */
  const truncate = (text, maxLength = 80) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const hasApiKey = !!user?.infinitagents_api_key;

  // ============================================
  // STYLES
  // ============================================

  const styles = {
    container: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgSecondary,
      height: '100vh',
      overflow: 'hidden',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 24px',
      backgroundColor: colors.headerBg,
      borderBottom: `1px solid ${colors.borderColor}`,
      minHeight: '60px',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    headerIcon: {
      width: '40px',
      height: '40px',
      backgroundColor: colors.accentPrimary,
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
    },
    headerTitle: {
      display: 'flex',
      flexDirection: 'column',
    },
    title: {
      fontSize: '18px',
      fontWeight: 600,
      color: colors.textPrimary,
      margin: 0,
    },
    subtitle: {
      fontSize: '13px',
      color: colors.textSecondary,
      margin: 0,
    },
    content: {
      flex: 1,
      padding: '24px',
      overflowY: 'auto',
    },
    infoBox: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '16px 20px',
      backgroundColor: `${colors.accentPrimary}10`,
      borderRadius: '12px',
      marginBottom: '24px',
      border: `1px solid ${colors.accentPrimary}30`,
    },
    infoIcon: {
      color: colors.accentPrimary,
      flexShrink: 0,
      marginTop: '2px',
    },
    infoText: {
      fontSize: '14px',
      color: colors.textPrimary,
      lineHeight: 1.5,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '16px',
    },
    card: {
      backgroundColor: colors.bgPrimary,
      borderRadius: '12px',
      padding: '20px',
      border: `2px solid ${colors.borderColor}`,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative',
    },
    cardSelected: {
      borderColor: colors.accentPrimary,
      backgroundColor: `${colors.accentPrimary}08`,
      boxShadow: `0 0 0 3px ${colors.accentPrimary}20`,
    },
    cardContent: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '14px',
    },
    avatar: {
      width: '52px',
      height: '52px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '18px',
      fontWeight: 600,
      flexShrink: 0,
      overflow: 'hidden',
    },
    avatarImg: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    agentInfo: {
      flex: 1,
      minWidth: 0,
    },
    agentName: {
      fontSize: '16px',
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '6px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    selectedBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '11px',
      fontWeight: 500,
      color: colors.accentPrimary,
      backgroundColor: `${colors.accentPrimary}15`,
      padding: '2px 8px',
      borderRadius: '10px',
    },
    agentDesc: {
      fontSize: '13px',
      color: colors.textSecondary,
      lineHeight: 1.4,
    },
    // Center states
    centerState: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px',
      textAlign: 'center',
    },
    stateIcon: {
      width: '64px',
      height: '64px',
      borderRadius: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '20px',
    },
    stateTitle: {
      fontSize: '18px',
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '8px',
    },
    stateText: {
      fontSize: '14px',
      color: colors.textSecondary,
      maxWidth: '400px',
      lineHeight: 1.5,
    },
    setupBtn: {
      marginTop: '20px',
      padding: '12px 28px',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    loader: {
      animation: 'spin 1s linear infinite',
    },
    // Popup / Modal
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    popup: {
      backgroundColor: colors.bgPrimary,
      borderRadius: '16px',
      width: '100%',
      maxWidth: '500px',
      maxHeight: '80vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      border: `1px solid ${colors.borderColor}`,
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    },
    popupHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '20px 24px',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    popupHeaderInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
    },
    popupBody: {
      flex: 1,
      padding: '24px',
      overflowY: 'auto',
    },
    popupFooter: {
      padding: '16px 24px',
      borderTop: `1px solid ${colors.borderColor}`,
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '12px',
    },
    sectionLabel: {
      fontSize: '13px',
      fontWeight: 600,
      color: colors.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '12px',
    },
    radioGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      marginBottom: '24px',
    },
    radioOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px 14px',
      borderRadius: '10px',
      border: `1.5px solid ${colors.borderColor}`,
      cursor: 'pointer',
      transition: 'all 0.15s ease',
    },
    radioOptionSelected: {
      borderColor: colors.accentPrimary,
      backgroundColor: `${colors.accentPrimary}08`,
    },
    radioDot: {
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      border: `2px solid ${colors.borderColor}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    radioDotSelected: {
      borderColor: colors.accentPrimary,
    },
    radioDotInner: {
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      backgroundColor: colors.accentPrimary,
    },
    radioLabel: {
      fontSize: '14px',
      fontWeight: 500,
      color: colors.textPrimary,
    },
    radioDesc: {
      fontSize: '12px',
      color: colors.textSecondary,
      marginTop: '2px',
    },
    contactsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
    contactItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 14px',
      backgroundColor: colors.bgSecondary,
      borderRadius: '8px',
    },
    contactPhone: {
      fontSize: '14px',
      color: colors.textPrimary,
      fontFamily: 'monospace',
    },
    removeBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '4px',
      borderRadius: '6px',
      color: colors.textTertiary,
      display: 'flex',
      alignItems: 'center',
    },
    emptyContacts: {
      textAlign: 'center',
      padding: '20px',
      color: colors.textTertiary,
      fontSize: '13px',
      lineHeight: 1.5,
    },
    cancelBtn: {
      padding: '10px 20px',
      backgroundColor: 'transparent',
      color: colors.textSecondary,
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
    },
    saveBtn: {
      padding: '10px 24px',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '6px',
      borderRadius: '8px',
      color: colors.textSecondary,
      display: 'flex',
      alignItems: 'center',
    },
    autopilotBadge: {
      fontSize: '11px',
      fontWeight: 500,
      padding: '3px 10px',
      borderRadius: '10px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
    },
  };

  // ============================================
  // RENDER: Header (reusable)
  // ============================================

  const renderHeader = () => (
    <div style={styles.header}>
      <div style={styles.headerLeft}>
        <div style={styles.headerIcon}>
          <Bot size={20} />
        </div>
        <div style={styles.headerTitle}>
          <h1 style={styles.title}>Meus Agentes</h1>
          <p style={styles.subtitle}>Configure o atendimento automático</p>
        </div>
      </div>
    </div>
  );

  // ============================================
  // RENDER: Autopilot Config Popup
  // ============================================

  const renderPopup = () => {
    if (!showPopup || !selectedAgent) return null;

    const isAllMode = autopilotMode === 'all';
    const logoUrl = selectedAgent.widget_settings?.logo_url;

    return (
      <div style={styles.overlay} onClick={() => setShowPopup(false)}>
        <div style={styles.popup} onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div style={styles.popupHeader}>
            <div style={styles.popupHeaderInfo}>
              <div
                style={{
                  ...styles.avatar,
                  width: '44px',
                  height: '44px',
                  fontSize: '16px',
                  backgroundColor: logoUrl ? 'transparent' : getAvatarColor(selectedAgent.name),
                }}
              >
                {logoUrl ? (
                  <img src={logoUrl} alt={selectedAgent.name} style={styles.avatarImg} />
                ) : (
                  getInitials(selectedAgent.name)
                )}
              </div>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
                  {selectedAgent.name}
                </div>
                <div style={{ fontSize: '13px', color: colors.textSecondary, marginTop: '2px' }}>
                  {truncate(selectedAgent.description, 60) || 'Sem descrição'}
                </div>
              </div>
            </div>
            <button style={styles.closeBtn} onClick={() => setShowPopup(false)}>
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div style={styles.popupBody}>
            {/* Simple switch for autopilot mode */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px',
              backgroundColor: isAllMode ? `${colors.accentPrimary}08` : colors.bgSecondary,
              borderRadius: '12px',
              border: `1px solid ${isAllMode ? `${colors.accentPrimary}30` : colors.borderColor}`,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }} onClick={() => setAutopilotMode(isAllMode ? 'specific' : 'all')}>
              <div style={{ flex: 1, marginRight: '16px' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: colors.textPrimary, marginBottom: '4px' }}>
                  Agente responde todos os contatos
                </div>
                <div style={{ fontSize: '12px', color: colors.textSecondary, lineHeight: '1.4' }}>
                  {isAllMode
                    ? 'O agente responde automaticamente todas as mensagens recebidas.'
                    : 'Inativo. O agente responde apenas contatos habilitados manualmente no chat.'
                  }
                </div>
              </div>
              {/* Toggle switch */}
              <div style={{
                width: '44px',
                height: '24px',
                borderRadius: '12px',
                backgroundColor: isAllMode ? colors.accentPrimary : `${colors.textTertiary}40`,
                position: 'relative',
                flexShrink: 0,
                transition: 'background-color 0.2s ease',
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  position: 'absolute',
                  top: '2px',
                  left: isAllMode ? '22px' : '2px',
                  transition: 'left 0.2s ease',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={styles.popupFooter}>
            <button style={styles.cancelBtn} onClick={() => setShowPopup(false)}>
              Cancelar
            </button>
            <button
              style={{
                ...styles.saveBtn,
                opacity: saving ? 0.7 : 1,
                pointerEvents: saving ? 'none' : 'auto',
              }}
              onClick={handleSaveConfig}
            >
              {saving ? (
                <Loader2 size={16} style={styles.loader} />
              ) : (
                <Settings size={16} />
              )}
              Salvar configuração
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER: States
  // ============================================

  // Loading
  if (loading) {
    return (
      <div style={styles.container}>
        {renderHeader()}
        <div style={styles.centerState}>
          <Loader2 size={40} color={colors.accentPrimary} style={styles.loader} />
          <p style={{ ...styles.stateText, marginTop: '16px' }}>Carregando agentes...</p>
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // No API Key or no agents → redirect to onboarding
  if (!hasApiKey || agents.length === 0) {
    return (
      <div style={styles.container}>
        {renderHeader()}
        <div style={styles.centerState}>
          <div style={{ ...styles.stateIcon, backgroundColor: `${colors.accentPrimary}15` }}>
            <Bot size={32} color={colors.accentPrimary} />
          </div>
          <h2 style={styles.stateTitle}>
            {!hasApiKey ? 'Agente ainda não configurado' : 'Nenhum agente encontrado'}
          </h2>
          <p style={styles.stateText}>
            {!hasApiKey
              ? 'Parece que seu agente ainda não foi configurado. Clique abaixo para iniciar a configuração.'
              : 'Crie seu primeiro agente para começar a usar o atendimento automático.'
            }
          </p>
          <button style={styles.setupBtn} onClick={handleSetupAgent}>
            <Zap size={18} />
            Configurar agente
          </button>
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        {renderHeader()}
        <div style={styles.centerState}>
          <div style={{ ...styles.stateIcon, backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
            <AlertCircle size={32} color="#EF4444" />
          </div>
          <h2 style={styles.stateTitle}>Erro ao carregar agentes</h2>
          <p style={styles.stateText}>{error}</p>
          <button
            onClick={fetchAgents}
            style={{
              marginTop: '16px',
              padding: '10px 20px',
              backgroundColor: colors.accentPrimary,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Agents grid with popup
  // ============================================

  /**
   * Get autopilot badge for card
   */
  const getModeBadge = () => {
    if (autopilotMode === 'all') {
      return (
        <span style={{
          ...styles.autopilotBadge,
          color: '#10B981',
          backgroundColor: '#10B98115',
        }}>
          <Zap size={11} /> Ativo
        </span>
      );
    }
    return (
      <span style={{
        ...styles.autopilotBadge,
        color: colors.textTertiary,
        backgroundColor: `${colors.textTertiary}15`,
      }}>
        Inativo
      </span>
    );
  };

  return (
    <div style={styles.container}>
      {renderHeader()}

      {/* Content */}
      <div style={styles.content}>
        {/* Info box */}
        <div style={styles.infoBox}>
          <Bot size={20} style={styles.infoIcon} />
          <p style={styles.infoText}>
            <strong>Clique no agente</strong> para ativar ou desativar o atendimento automático.
            Você pode controlar contatos individuais diretamente no chat.
          </p>
        </div>

        {/* Agents grid */}
        <div style={styles.grid}>
          {agents.map((agent) => {
            const isSelected = activeAgentId === agent.id;
            const logoUrl = agent.widget_settings?.logo_url;

            return (
              <div
                key={agent.id}
                onClick={() => handleAgentClick(agent)}
                style={{
                  ...styles.card,
                  ...(isSelected ? styles.cardSelected : {}),
                }}
              >
                <div style={styles.cardContent}>
                  {/* Avatar */}
                  <div
                    style={{
                      ...styles.avatar,
                      backgroundColor: logoUrl ? 'transparent' : getAvatarColor(agent.name),
                    }}
                  >
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt={agent.name}
                        style={styles.avatarImg}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.style.backgroundColor = getAvatarColor(agent.name);
                          e.target.parentElement.innerHTML = getInitials(agent.name);
                        }}
                      />
                    ) : (
                      getInitials(agent.name)
                    )}
                  </div>

                  {/* Info */}
                  <div style={styles.agentInfo}>
                    <div style={styles.agentName}>
                      {agent.name}
                      {isSelected && (
                        <span style={styles.selectedBadge}>
                          <CheckCircle2 size={12} />
                          Ativo
                        </span>
                      )}
                    </div>
                    <p style={styles.agentDesc}>
                      {truncate(agent.description) || 'Sem descrição'}
                    </p>
                    {/* Autopilot mode badge */}
                    {isSelected && (
                      <div style={{ marginTop: '8px' }}>
                        {getModeBadge()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Autopilot Config Popup */}
      {renderPopup()}

      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AgentsPage;
